import {
  PageBlueprint,
  createFrontendModule,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { RiOrganizationChart } from '@remixicon/react';
import { orgChartRouteRef } from './routes';

/**
 * Renders the Group's `spec.contacts` block, which is a custom field: the
 * Backstage Group schema does not know it, but it does not forbid it either.
 * The card only attaches to groups that actually carry contacts.
 */
const EntityContactsCard = EntityCardBlueprint.make({
  name: 'contacts',
  params: {
    type: 'info',
    filter: entity => entity.kind === 'Group' && Boolean(entity.spec?.contacts),
    loader: () => import('./ContactsCard').then(m => <m.ContactsCard />),
  },
});

/** Path from the root, sub-groups, and the way onto the `/org` chart. */
const EntityOrgPathCard = EntityCardBlueprint.make({
  name: 'org-path',
  params: {
    type: 'info',
    filter: entity => entity.kind === 'Group',
    loader: () => import('./OrgPathCard').then(m => <m.OrgPathCard />),
  },
});

/** `spec.responsibilities`, custom like `spec.contacts`; 51 of 81 Groups have it. */
const EntityResponsibilitiesCard = EntityCardBlueprint.make({
  name: 'responsibilities',
  params: {
    type: 'info',
    filter: entity =>
      entity.kind === 'Group' && Boolean(entity.spec?.responsibilities),
    loader: () =>
      import('./ResponsibilitiesCard').then(m => <m.ResponsibilitiesCard />),
  },
});

export const orgModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    EntityOrgPathCard,
    EntityResponsibilitiesCard,
    EntityContactsCard,
  ],
});

const ORG_CHART_FLAG = 'org-chart';

/**
 * The whole org chart as one picture, at `/org`. A plugin of its own rather
 * than a page inside `catalog`, so its header and sidebar entry are its own.
 * Behind a feature flag, per user in Settings → Feature Flags: with it off
 * the page, its sidebar item and the Group card's «На оргсхеме» button are
 * all gone — the button hides itself when the route does not resolve.
 */
const orgChartPage = PageBlueprint.make({
  if: { featureFlags: { $contains: ORG_CHART_FLAG } },
  params: {
    path: '/org',
    routeRef: orgChartRouteRef,
    loader: () => import('./OrgChartPage').then(m => <m.OrgChartPage />),
  },
});

export const orgChartPlugin = createFrontendPlugin({
  pluginId: 'org-chart',
  title: 'Оргструктура',
  icon: <RiOrganizationChart />,
  routes: { root: orgChartRouteRef },
  extensions: [orgChartPage],
  featureFlags: [
    {
      name: ORG_CHART_FLAG,
      description: 'Show the org chart page (/org) and its sidebar item',
    },
  ],
});
